// 2.6.0 : Caisse du bureau (comptes khaldoun et Moha seulement ; le serveur refuse tout autre compte).
// - Code PIN à chaque ouverture (jeton de caisse gardé en mémoire seulement, jamais dans le téléphone).
// - Vue du jour sans aucun total (vue de Moha, et vue discrète de khaldoun).
// - Le mot secret du gérant, tapé dans « Rechercher » puis Entrée, ouvre la vue complète ; retour automatique
//   à la vue discrète après 3 minutes sans toucher l'écran.
// - Clôture à l'aveugle : Moha saisit le montant compté, sans voir le montant attendu.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, Lock, Delete, Plus, Minus, X, Loader2, Search, MoreHorizontal, FileDown, History,
  KeyRound, ListChecks, EyeOff, RotateCcw,
} from "lucide-react";
import { API, FOCUS, entetes, estNonAutorise, messageErreur } from "./outils";

const COMPLET_MS = 3 * 60 * 1000;
const ESPECES = "Espèces";

export const da = (v) => {
  const n = Number(v) || 0;
  const s = `${Math.abs(n).toLocaleString("fr-FR", { maximumFractionDigits: 0 }).replace(/ | /g, " ")} DA`;
  return n < 0 ? `−${s}` : s;
};
const jourFr = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const isoLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const decaler = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return isoLocal(d);
};
const lundi = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  const w = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - w);
  return isoLocal(d);
};
const premierDuMois = (iso) => `${iso.slice(0, 8)}01`;
const finDuMois = (iso) => {
  const d = new Date(`${iso.slice(0, 8)}01T12:00:00`);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return isoLocal(d);
};
const etatPaiement = (p) => {
  if (!p) return null;
  if (p.startsWith("acompte:")) {
    const r = p.split(":")[1];
    return { t: r ? `Acompte · reste ${da(r)}` : "Acompte · reste non saisi", c: "bg-[#FFEDD5] text-[#C2410C]" };
  }
  if (p === "total") return { t: "Totalement payé", c: "bg-[#DCFCE7] text-[#15803D]" };
  if (p === "non_paye") return { t: "Non payé", c: "bg-[#F1F5F9] text-[#475569]" };
  return null;
};

function Etiquette({ l }) {
  if (l.sens === "sortie") return <span className="text-[10.5px] font-bold px-1.5 rounded-md bg-[#FEE2E2] text-[#B91C1C]">Sortie</span>;
  if (l.source === "fiche") return <span className="text-[10.5px] font-bold px-1.5 rounded-md bg-[#CCFBF1] text-[#0F766E]">Fiche</span>;
  return <span className="text-[10.5px] font-bold px-1.5 rounded-md bg-[#EDE9FE] text-[#6D28D9]">{l.categorie}</span>;
}

function EnTete({ titre, sousTitre, onRetour, droite, enfant }) {
  return (
    <header className="flex-none sticky top-0 z-10 bg-[#0F172A] px-4 pb-3 pt-[max(12px,env(safe-area-inset-top))] flex flex-col gap-2.5">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onRetour} aria-label="Retour"
          className={`w-11 h-11 -ml-2.5 flex-none flex items-center justify-center text-[#E2E8F0] rounded-lg ${FOCUS}`}>
          <ChevronLeft aria-hidden="true" className="w-[22px] h-[22px]" />
        </button>
        <div className="flex flex-col flex-1 min-w-0">
          <h1 className="m-0 text-white font-bold text-base leading-tight truncate">{titre}</h1>
          {sousTitre ? <span className="text-[#94A3B8] text-xs truncate">{sousTitre}</span> : null}
        </div>
        {droite}
      </div>
      {enfant}
    </header>
  );
}

function Feuille({ titre, onFermer, children }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" role="dialog" aria-modal="true" aria-label={titre}>
      <button type="button" aria-label="Fermer" className="absolute inset-0 bg-[#0F172A]/50" onClick={onFermer} />
      <div className="relative bg-white rounded-t-[20px] max-h-[92dvh] overflow-y-auto p-4 pb-[max(16px,env(safe-area-inset-bottom))]">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="m-0 flex-1 text-[16px] font-bold">{titre}</h2>
          <button type="button" onClick={onFermer} aria-label="Fermer"
            className={`w-10 h-10 flex items-center justify-center rounded-lg text-[#475569] ${FOCUS}`}>
            <X aria-hidden="true" className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const Champ = ({ label, children }) => (
  <label className="flex flex-col gap-1 mt-2.5">
    <span className="text-xs font-semibold text-[#475569]">{label}</span>
    {children}
  </label>
);
const CLS_INPUT = "border border-[#CBD5E1] rounded-[10px] px-3 h-11 text-[15px] bg-white focus:outline-none focus:border-[#14B8A6]";

function Puces({ options, valeur, onChoix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChoix(o)} aria-pressed={valeur === o}
          className={`px-3 h-9 rounded-full text-[13px] font-semibold border ${valeur === o ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white text-[#0F172A] border-[#E2E8F0]"} ${FOCUS}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Code PIN
// ---------------------------------------------------------------------------
function EcranPin({ etat, onValider, erreur, occupe, onRetour }) {
  const creation = !etat.pin_defini;
  const [pin, setPin] = useState("");
  const [premier, setPremier] = useState("");
  const [secret, setSecret] = useState("");
  const [etape, setEtape] = useState(creation ? "nouveau" : "saisie"); // nouveau -> confirmer -> secret
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (erreur) setPin("");
  }, [erreur]);

  const taper = (c) => {
    if (occupe) return;
    setMsg("");
    if (c === "x") return setPin((p) => p.slice(0, -1));
    const suite = (pin + c).slice(0, 4);
    setPin(suite);
    if (suite.length < 4) return;
    if (etape === "saisie") {
      onValider(suite, "");
    } else if (etape === "nouveau") {
      setPremier(suite);
      setPin("");
      setEtape("confirmer");
    } else if (etape === "confirmer") {
      if (suite !== premier) {
        setMsg("Les deux codes sont différents. Recommencez.");
        setPin("");
        setPremier("");
        setEtape("nouveau");
      } else if (etat.secret_a_definir) {
        setEtape("secret");
      } else {
        onValider(suite, "");
      }
    }
  };

  const titres = {
    saisie: "Entrez votre code PIN",
    nouveau: "Choisissez votre code PIN (4 chiffres)",
    confirmer: "Confirmez votre code PIN",
  };

  if (etape === "secret") {
    return (
      <div className="ecran flex flex-col bg-[#0F172A] text-white px-6 pt-[max(40px,env(safe-area-inset-top))]" data-testid="caisse-secret-init">
        <KeyRound aria-hidden="true" className="w-8 h-8 text-[#5EEAD4] mx-auto" />
        <h1 className="text-lg font-bold text-center mt-3 mb-1">Votre mot secret</h1>
        <p className="text-[13px] text-[#94A3B8] text-center m-0">
          Tapé dans « Rechercher » de la caisse, il ouvre la vue complète. Ne le donnez à personne. Au moins 4 caractères.
        </p>
        <input type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)}
          className="mt-5 h-12 rounded-xl bg-[#1E293B] border border-[#334155] px-4 text-white text-base focus:outline-none focus:border-[#2DD4BF]"
          aria-label="Mot secret" data-testid="caisse-secret-champ" />
        {erreur ? <p role="alert" className="text-[#FCA5A5] text-sm text-center mt-3">{erreur}</p> : null}
        <button type="button" disabled={occupe || secret.trim().length < 4} onClick={() => onValider(premier, secret)}
          className={`mt-5 h-12 rounded-xl bg-[#0F766E] font-bold disabled:opacity-50 ${FOCUS}`}>
          {occupe ? "…" : "Enregistrer"}
        </button>
      </div>
    );
  }

  return (
    <div className="ecran flex flex-col items-center bg-[#0F172A] text-white px-6 pt-[max(48px,env(safe-area-inset-top))]" data-testid="caisse-pin">
      <Lock aria-hidden="true" className="w-8 h-8 text-[#E2E8F0]" />
      <h1 className="text-lg font-bold mt-3 mb-1">Caisse</h1>
      <p className="m-0 text-[13px] text-[#94A3B8] text-center">{titres[etape]}</p>
      <div className="flex gap-4 my-7" aria-label={`${pin.length} chiffre(s) sur 4`}>
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`w-3.5 h-3.5 rounded-full border-2 ${i < pin.length ? "bg-white border-white" : "border-[#CBD5E1]"}`} />
        ))}
      </div>
      {etat.bloque_min ? (
        <p role="alert" className="text-[#FCA5A5] text-sm text-center -mt-3 mb-4">Caisse bloquée encore {etat.bloque_min} min.</p>
      ) : null}
      {erreur || msg ? <p role="alert" className="text-[#FCA5A5] text-sm text-center -mt-3 mb-4">{erreur || msg}</p> : null}
      <div className="grid grid-cols-3 gap-x-6 gap-y-3">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((c) => (
          <button key={c} type="button" onClick={() => taper(c)} data-testid={`pin-${c}`}
            className={`w-[68px] h-[68px] rounded-full bg-[#1E293B] text-2xl font-semibold active:bg-[#334155] ${FOCUS}`}>
            {c}
          </button>
        ))}
        <button type="button" onClick={onRetour} className={`w-[68px] h-[68px] text-sm text-[#94A3B8] ${FOCUS}`}>Annuler</button>
        <button type="button" onClick={() => taper("0")} data-testid="pin-0"
          className={`w-[68px] h-[68px] rounded-full bg-[#1E293B] text-2xl font-semibold active:bg-[#334155] ${FOCUS}`}>0</button>
        <button type="button" onClick={() => taper("x")} aria-label="Effacer"
          className={`w-[68px] h-[68px] flex items-center justify-center text-[#94A3B8] ${FOCUS}`}>
          <Delete aria-hidden="true" className="w-6 h-6" />
        </button>
      </div>
      {occupe ? <Loader2 aria-hidden="true" className="w-5 h-5 animate-spin mt-6 text-[#94A3B8]" /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formulaires
// ---------------------------------------------------------------------------
function FormMouvement({ sens, ligne, listes, onEnregistrer, onSupprimer, onFermer, occupe, erreur, jourFixe, dateModifiable }) {
  const options = (listes && listes[sens]) || [];
  const [categorie, setCategorie] = useState(ligne ? ligne.categorie : "");
  const [client, setClient] = useState(ligne ? ligne.client : "");
  const [benef, setBenef] = useState(ligne ? ligne.beneficiaire : "");
  const [montant, setMontant] = useState(ligne ? String(ligne.montant) : "");
  const [mode, setMode] = useState(ligne ? ligne.mode : ESPECES);
  const [remarque, setRemarque] = useState(ligne ? ligne.remarque : "");
  const [jourLigne, setJourLigne] = useState(ligne ? ligne.jour : "");   // 2.6.6 : déplacer la ligne (administrateur)
  const [confirmer, setConfirmer] = useState(false);
  const opts = ligne && !options.includes(ligne.categorie) ? [ligne.categorie, ...options] : options;
  const pret = categorie && Number(String(montant).replace(/\s/g, "").replace(",", ".")) > 0 && (sens === "sortie" || client.trim());
  const titre = (ligne ? "Modifier la ligne" : sens === "sortie" ? "− Nouvelle sortie" : "+ Nouvel encaissement")
    + (jourFixe ? ` · ${jourFr(jourFixe)}` : "");
  return (
    <Feuille titre={titre} onFermer={onFermer}>
      {jourFixe ? (
        <p className="m-0 mb-1 text-[12.5px] bg-[#FEF3C7] text-[#92400E] rounded-lg px-3 py-2">
          Opération enregistrée à la date du <b>{jourFr(jourFixe)}</b> (journée affichée).
        </p>
      ) : null}
      {ligne && dateModifiable ? (
        <Champ label="Date de l'opération">
          <input type="date" className={CLS_INPUT} value={jourLigne} onChange={(e) => setJourLigne(e.target.value)} data-testid="caisse-jour" />
        </Champ>
      ) : null}
      <Champ label={sens === "sortie" ? "Motif" : "Service"}>
        <Puces options={opts} valeur={categorie} onChoix={setCategorie} />
      </Champ>
      {sens === "entree" ? (
        <Champ label="Client (nom et prénom)">
          <input className={CLS_INPUT} value={client} onChange={(e) => setClient(e.target.value)} autoComplete="off" data-testid="caisse-client" />
        </Champ>
      ) : (
        <Champ label="Bénéficiaire (facultatif)">
          <input className={CLS_INPUT} value={benef} onChange={(e) => setBenef(e.target.value)} autoComplete="off" />
        </Champ>
      )}
      <Champ label="Montant (DA)">
        <input className={`${CLS_INPUT} text-right font-bold`} inputMode="numeric" value={montant} data-testid="caisse-montant"
          onChange={(e) => setMontant(e.target.value.replace(/[^\d\s,.]/g, ""))} />
      </Champ>
      <Champ label="Mode de paiement">
        <Puces options={(listes && listes.modes) || [ESPECES]} valeur={mode} onChoix={setMode} />
      </Champ>
      <Champ label="Remarque (facultatif)">
        <input className={CLS_INPUT} value={remarque} onChange={(e) => setRemarque(e.target.value)} autoComplete="off" />
      </Champ>
      {erreur ? <p role="alert" className="text-[#B91C1C] text-sm mt-3 mb-0">{erreur}</p> : null}
      <div className="flex gap-2 mt-4">
        {ligne && onSupprimer ? (
          confirmer ? (
            <button type="button" disabled={occupe} onClick={onSupprimer}
              className={`flex-1 h-12 rounded-xl bg-[#B91C1C] text-white font-bold ${FOCUS}`}>Confirmer la suppression</button>
          ) : (
            <button type="button" onClick={() => setConfirmer(true)}
              className={`h-12 px-4 rounded-xl bg-[#FEE2E2] text-[#B91C1C] font-bold ${FOCUS}`}>Supprimer</button>
          )
        ) : null}
        {!confirmer ? (
          <button type="button" disabled={!pret || occupe} data-testid="caisse-enregistrer"
            onClick={() => onEnregistrer({
              sens, categorie, client, beneficiaire: benef, montant, mode, remarque,
              ...(jourFixe ? { jour: jourFixe } : {}),
              ...(ligne && dateModifiable && jourLigne && jourLigne !== ligne.jour ? { jour: jourLigne } : {}),
            })}
            className={`flex-1 h-12 rounded-xl text-white font-bold disabled:opacity-50 ${sens === "sortie" ? "bg-[#B91C1C]" : "bg-[#0F766E]"} ${FOCUS}`}>
            {occupe ? "…" : "Enregistrer"}
          </button>
        ) : null}
      </div>
    </Feuille>
  );
}

function FormCredit({ credit, motifs, onEnregistrer, onSupprimer, onFermer, occupe, erreur }) {
  const [motif, setMotif] = useState(credit ? credit.motif : "");
  const [client, setClient] = useState(credit ? credit.client : "");
  const [montant, setMontant] = useState(credit ? String(credit.montant) : "");
  const [remarque, setRemarque] = useState(credit ? credit.remarque : "");
  const [telephone, setTelephone] = useState(credit ? credit.telephone || "" : "");
  const [confirmer, setConfirmer] = useState(false);
  const opts = credit && !motifs.includes(credit.motif) ? [credit.motif, ...motifs] : motifs;
  const pret = motif && client.trim() && Number(String(montant).replace(/\s/g, "").replace(",", ".")) > 0;
  return (
    <Feuille titre={credit ? "Modifier le crédit" : "+ Nouveau crédit"} onFermer={onFermer}>
      <Champ label="Motif"><Puces options={opts} valeur={motif} onChoix={setMotif} /></Champ>
      <Champ label="Client (nom et prénom)">
        <input className={CLS_INPUT} value={client} onChange={(e) => setClient(e.target.value)} autoComplete="off" data-testid="credit-client" />
      </Champ>
      <Champ label="N° WhatsApp du client (pour les rappels)">
        <input className={CLS_INPUT} type="tel" inputMode="tel" value={telephone} placeholder="0550 00 00 00" data-testid="credit-tel"
          onChange={(e) => setTelephone(e.target.value.replace(/[^\d\s+]/g, ""))} autoComplete="off" />
      </Champ>
      <Champ label="Montant dû (DA)">
        <input className={`${CLS_INPUT} text-right font-bold`} inputMode="numeric" value={montant} data-testid="credit-montant"
          onChange={(e) => setMontant(e.target.value.replace(/[^\d\s,.]/g, ""))} />
      </Champ>
      <Champ label="Remarque (facultatif)">
        <input className={CLS_INPUT} value={remarque} onChange={(e) => setRemarque(e.target.value)} autoComplete="off" />
      </Champ>
      {erreur ? <p role="alert" className="text-[#B91C1C] text-sm mt-3 mb-0">{erreur}</p> : null}
      <div className="flex gap-2 mt-4">
        {credit && onSupprimer ? (
          confirmer ? (
            <button type="button" disabled={occupe} onClick={onSupprimer}
              className={`flex-1 h-12 rounded-xl bg-[#B91C1C] text-white font-bold ${FOCUS}`}>Confirmer la suppression</button>
          ) : (
            <button type="button" onClick={() => setConfirmer(true)}
              className={`h-12 px-4 rounded-xl bg-[#FEE2E2] text-[#B91C1C] font-bold ${FOCUS}`}>Supprimer</button>
          )
        ) : null}
        {!confirmer ? (
          <button type="button" disabled={!pret || occupe} data-testid="credit-enregistrer"
            onClick={() => onEnregistrer({ motif, client, montant, remarque, telephone })}
            className={`flex-1 h-12 rounded-xl bg-[#6D28D9] text-white font-bold disabled:opacity-50 ${FOCUS}`}>
            {occupe ? "…" : "Enregistrer"}
          </button>
        ) : null}
      </div>
    </Feuille>
  );
}

function FormEncaisser({ credit, modes, onValider, onFermer, occupe, erreur }) {
  const [partiel, setPartiel] = useState(false);
  const [montant, setMontant] = useState(String(credit.montant));
  const [mode, setMode] = useState(ESPECES);
  const m = Number(String(montant).replace(/\s/g, "").replace(",", "."));
  const ok = m > 0 && m <= credit.montant;
  return (
    <Feuille titre={`Encaisser · ${credit.client}`} onFermer={onFermer}>
      <p className="m-0 text-[13px] text-[#475569]">{credit.motif} · reste dû <b>{da(credit.montant)}</b></p>
      <Champ label="Paiement">
        <Puces options={[`Tout (${da(credit.montant)})`, "Partiel"]} valeur={partiel ? "Partiel" : `Tout (${da(credit.montant)})`}
          onChoix={(o) => { const p = o === "Partiel"; setPartiel(p); setMontant(p ? "" : String(credit.montant)); }} />
      </Champ>
      <Champ label="Montant encaissé (DA)">
        <input className={`${CLS_INPUT} h-14 text-2xl text-right font-bold`} inputMode="numeric" value={montant} disabled={!partiel}
          data-testid="encaisser-montant" onChange={(e) => setMontant(e.target.value.replace(/[^\d\s,.]/g, ""))} />
      </Champ>
      <Champ label="Mode de paiement"><Puces options={modes} valeur={mode} onChoix={setMode} /></Champ>
      {partiel && m > 0 && m < credit.montant ? (
        <p className="mt-2 mb-0 text-[12.5px] text-[#475569]">Il restera {da(credit.montant - m)} à payer.</p>
      ) : null}
      {credit.source === "fiche" ? (
        <p className="mt-3 mb-0 text-[12px] bg-[#CCFBF1] text-[#134E4A] rounded-lg px-3 py-2">
          La fiche est mise à jour (versé et reste) et l'entrée apparaît dans la caisse du jour.
        </p>
      ) : null}
      {erreur ? <p role="alert" className="text-[#B91C1C] text-sm mt-3 mb-0">{erreur}</p> : null}
      <button type="button" disabled={!ok || occupe} onClick={() => onValider(String(m), mode)} data-testid="encaisser-valider"
        className={`w-full mt-4 h-12 rounded-xl bg-[#0F766E] text-white font-bold disabled:opacity-50 ${FOCUS}`}>
        {occupe ? "…" : `Encaisser ${ok ? da(m) : ""}`}
      </button>
    </Feuille>
  );
}

// 2.6.9 : cases « Entrées » / « Sorties » du jour (total + nombre d'opérations) ; un toucher montre le détail
export function totauxSens(lignes) {
  const t = { entree: { total: 0, n: 0 }, sortie: { total: 0, n: 0 } };
  (lignes || []).forEach((l) => {
    const k = l.sens === "sortie" ? "sortie" : "entree";
    t[k].total += Number(l.montant) || 0;
    t[k].n += 1;
  });
  return t;
}

function CasesSens({ lignes, ouvert, onChoisir }) {
  const t = totauxSens(lignes);
  const carte = (k, titre, cls, clsTitre) => (
    <button type="button" key={k} onClick={() => onChoisir(k)} aria-pressed={ouvert === k} data-testid={`caisse-case-${k}`}
      className={`text-left rounded-xl px-3 py-2 border-[1.5px] ${cls} ${ouvert === k ? "ring-2 ring-offset-1 ring-[#0F172A]" : ""} ${FOCUS}`}>
      <span className="flex items-center justify-between">
        <span className={`text-[12.5px] font-bold ${clsTitre}`}>{titre}</span>
        <ChevronRight aria-hidden="true" className="w-4 h-4 text-[#475569]" />
      </span>
      <b className="block text-[19px] tabular-nums leading-tight">{da(t[k].total)}</b>
      <span className="text-[11.5px] text-[#475569]">{t[k].n} opération{t[k].n > 1 ? "s" : ""}</span>
    </button>
  );
  return (
    <div className="flex-none grid grid-cols-2 gap-2" data-testid="caisse-cases">
      {carte("entree", "Entrées", "bg-[#F0FDF4] border-[#86EFAC]", "text-[#15803D]")}
      {carte("sortie", "Sorties", "bg-[#FEF2F2] border-[#FCA5A5]", "text-[#B91C1C]")}
    </div>
  );
}

function DetailSens({ lignes, sens }) {
  const choisies = (lignes || []).filter((l) => (l.sens === "sortie" ? "sortie" : "entree") === sens);
  const total = choisies.reduce((a, l) => a + (Number(l.montant) || 0), 0);
  return (
    <div className="flex flex-col gap-1.5" data-testid={`caisse-detail-${sens}`}>
      {!choisies.length ? (
        <p className="m-0 p-3 text-center text-sm text-[#64748B]">{sens === "sortie" ? "Aucune sortie." : "Aucune entrée."}</p>
      ) : choisies.map((l) => <Ligne key={l.id} l={l} />)}
      <div className="flex justify-between font-bold text-[14px] border-t-2 border-[#E2E8F0] pt-2 mt-1">
        <span>{sens === "sortie" ? "Total des sorties" : "Total des entrées"}</span>
        <span className={`tabular-nums ${sens === "sortie" ? "text-[#B91C1C]" : "text-[#15803D]"}`}>{da(total)}</span>
      </div>
    </div>
  );
}

function FormCloture({ onValider, onFermer, occupe, erreur, jourFixe, lignes }) {
  const [remis, setRemis] = useState("");
  const [detail, setDetail] = useState("");
  return (
    <Feuille titre={jourFixe ? `Clôturer la journée du ${jourFr(jourFixe)}` : "Clôturer la journée"} onFermer={onFermer}>
      {lignes ? (
        <div className="mb-2 flex flex-col gap-2">
          <CasesSens lignes={lignes} ouvert={detail} onChoisir={(k) => setDetail(detail === k ? "" : k)} />
          {detail ? <DetailSens lignes={lignes} sens={detail} /> : null}
        </div>
      ) : null}
      <p className="m-0 text-[13px] text-[#475569]">
        Comptez les espèces de la caisse, puis saisissez le montant que vous remettez à khaldoun.
      </p>
      <Champ label="Montant compté et remis (DA)">
        <input className={`${CLS_INPUT} h-14 text-2xl text-right font-bold`} inputMode="numeric" value={remis} data-testid="caisse-remis"
          onChange={(e) => setRemis(e.target.value.replace(/[^\d\s,.]/g, ""))} />
      </Champ>
      <p className="mt-3 mb-0 text-[12.5px] bg-[#FFEDD5] text-[#7C2D12] rounded-lg px-3 py-2">
        Après validation, la journée est fermée. Seul khaldoun peut la rouvrir.
      </p>
      {erreur ? <p role="alert" className="text-[#B91C1C] text-sm mt-3 mb-0">{erreur}</p> : null}
      <button type="button" disabled={!remis.trim() || occupe} onClick={() => onValider(remis)} data-testid="caisse-valider-cloture"
        className={`w-full mt-4 h-12 rounded-xl bg-[#6D28D9] text-white font-bold disabled:opacity-50 ${FOCUS}`}>
        {occupe ? "…" : "Valider la clôture"}
      </button>
    </Feuille>
  );
}

function FormChangerPin({ onValider, onFermer, occupe, erreur }) {
  const [ancien, setAncien] = useState("");
  const [nouveau, setNouveau] = useState("");
  const ok = /^\d{4}$/.test(ancien) && /^\d{4}$/.test(nouveau);
  return (
    <Feuille titre="Changer mon code PIN" onFermer={onFermer}>
      <Champ label="Code actuel">
        <input className={CLS_INPUT} type="password" inputMode="numeric" maxLength={4} value={ancien} onChange={(e) => setAncien(e.target.value.replace(/\D/g, ""))} />
      </Champ>
      <Champ label="Nouveau code (4 chiffres)">
        <input className={CLS_INPUT} type="password" inputMode="numeric" maxLength={4} value={nouveau} onChange={(e) => setNouveau(e.target.value.replace(/\D/g, ""))} />
      </Champ>
      {erreur ? <p role="alert" className="text-[#B91C1C] text-sm mt-3 mb-0">{erreur}</p> : null}
      <button type="button" disabled={!ok || occupe} onClick={() => onValider(ancien, nouveau)}
        className={`w-full mt-4 h-12 rounded-xl bg-[#0F766E] text-white font-bold disabled:opacity-50 ${FOCUS}`}>Enregistrer</button>
    </Feuille>
  );
}

// ---------------------------------------------------------------------------
// Lignes
// ---------------------------------------------------------------------------
function Ligne({ l, complet, onOuvrir, avecJour }) {
  const p = complet ? etatPaiement(l.paiement) : null;
  const montant = l.sens === "sortie" ? -l.montant : l.montant;
  const contenu = (
    <>
      <span className="flex flex-col flex-1 min-w-0 gap-0.5">
        <span className="flex items-center gap-1.5 min-w-0">
          <Etiquette l={l} />
          <span className="text-[14px] font-semibold truncate">{l.sens === "sortie" ? l.categorie : l.client || l.categorie}</span>
        </span>
        <span className="text-[11.5px] text-[#64748B] truncate">
          {avecJour ? `${jourFr(l.jour).slice(0, 5)} · ` : ""}{l.heure} · {l.agent} · {l.mode !== ESPECES ? (
            <span className="font-semibold text-[#0369A1]">{l.mode}</span>
          ) : l.mode}{l.detail ? ` · ${l.detail}` : ""}
        </span>
        {p ? <span className={`self-start text-[10.5px] font-bold px-1.5 rounded-md ${p.c}`}>{p.t}</span> : null}
      </span>
      <span className={`flex-none text-[14px] font-bold tabular-nums ${montant < 0 ? "text-[#B91C1C]" : "text-[#15803D]"}`}>{da(montant)}</span>
    </>
  );
  if (l.modifiable && onOuvrir) {
    return (
      <button type="button" onClick={() => onOuvrir(l)} data-testid="caisse-ligne"
        className={`w-full text-left bg-white border border-[#E2E8F0] rounded-xl px-3 py-2.5 flex items-start gap-2 ${FOCUS}`}>
        {contenu}
      </button>
    );
  }
  return <div className="bg-white border border-[#E2E8F0] rounded-xl px-3 py-2.5 flex items-start gap-2" data-testid="caisse-ligne">{contenu}</div>;
}

function Bloc({ titre, lignes }) {
  if (!lignes || !lignes.length) return null;
  return (
    <section className="bg-white border border-[#E2E8F0] rounded-xl px-3 py-2">
      <h3 className="m-0 mb-1 text-[13px] font-bold">{titre}</h3>
      {lignes.map(([a, b]) => (
        <div key={a} className="flex justify-between gap-2 py-1 text-[13px] border-t border-dashed border-[#E2E8F0] first-of-type:border-0">
          <span className="truncate">{a}</span><span className="font-semibold tabular-nums">{da(b)}</span>
        </div>
      ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Écran principal
// ---------------------------------------------------------------------------
export default function EcranCaisse({ code, onRetour, onLogout }) {
  const [etat, setEtat] = useState(null);
  const [jeton, setJeton] = useState("");
  const [erreurPin, setErreurPin] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [jour, setJour] = useState(null);
  const [erreur, setErreur] = useState("");
  const [recherche, setRecherche] = useState("");
  const [complet, setComplet] = useState(false);
  const [feuille, setFeuille] = useState(null); // {type, ...}
  const [erreurFeuille, setErreurFeuille] = useState("");
  const [vue, setVue] = useState(null);
  const [periode, setPeriode] = useState({ type: "jour", du: "", au: "" });
  const [reste, setReste] = useState(COMPLET_MS);
  const [menu, setMenu] = useState(false);
  const [onglet, setOnglet] = useState("caisse"); // 2.6.2 : « caisse » | « credits »
  const [listeCredits, setListeCredits] = useState(null);
  const derniereAction = useRef(Date.now());
  const dernierPing = useRef(0);
  const jetonRef = useRef("");

  const h = useCallback(() => ({ ...entetes(code), Authorization: `Caisse ${jetonRef.current}` }), [code]);

  const verrouiller = useCallback((msg) => {
    jetonRef.current = "";
    setJeton("");
    setComplet(false);
    setVue(null);
    setJour(null);
    setFeuille(null);
    setErreurPin(msg || "");
    axios.get(`${API}/mobile/caisse/acces`, { headers: entetes(code), timeout: 15000 }).then((r) => setEtat(r.data)).catch(() => {});
  }, [code]);

  // Erreur d'appel : PIN expiré -> écran PIN ; session de l'appli -> déconnexion ; vue complète fermée -> discret
  const traiter = useCallback((err, setMsg) => {
    const st = err?.response?.status;
    const d = String(err?.response?.data?.detail || "");
    if (st === 401 && d.startsWith("Caisse verrouillée")) return verrouiller("Caisse verrouillée : entrez votre code PIN.");
    if (st === 403 && d.startsWith("Vue complète fermée")) {
      setComplet(false);
      setVue(null);
      return undefined;
    }
    if (estNonAutorise(err)) return onLogout();
    if (st === 404 && d === "Introuvable") return onRetour();
    return setMsg(messageErreur(err));
  }, [verrouiller, onLogout, onRetour]);

  // Accès (bouton visible seulement si autorisé ; ici on relit l'état du PIN)
  useEffect(() => {
    axios
      .get(`${API}/mobile/caisse/acces`, { headers: entetes(code), timeout: 15000 })
      .then((r) => (r.data && r.data.acces ? setEtat(r.data) : onRetour()))
      .catch((err) => (estNonAutorise(err) ? onLogout() : setErreur(messageErreur(err))));
  }, [code, onRetour, onLogout]);

  // En quittant l'écran : le jeton de caisse est effacé côté serveur
  useEffect(() => () => {
    if (jetonRef.current) {
      axios.post(`${API}/mobile/caisse/sortir`, {}, { headers: { ...entetes(code), Authorization: `Caisse ${jetonRef.current}` }, timeout: 8000 }).catch(() => {});
    }
  }, [code]);

  const chargerJour = useCallback(() => {
    if (!jetonRef.current) return;
    axios
      .get(`${API}/mobile/caisse/jour`, { headers: h(), timeout: 20000 })
      .then((r) => {
        setJour(r.data);
        setErreur("");
      })
      .catch((err) => traiter(err, setErreur));
  }, [h, traiter]);

  const chargerCredits = useCallback(() => {
    if (!jetonRef.current) return;
    axios
      .get(`${API}/mobile/caisse/credits`, { headers: h(), timeout: 20000 })
      .then((r) => setListeCredits(r.data.credits || []))
      .catch((err) => traiter(err, setErreur));
  }, [h, traiter]);

  useEffect(() => {
    if (jeton && onglet === "credits") chargerCredits();
  }, [jeton, onglet, chargerCredits]);

  const bornes = useCallback((p, ref) => {
    const base = ref || (jour && jour.jour) || isoLocal(new Date());
    if (p.type === "jour") return [p.du || base, p.du || base];
    if (p.type === "semaine") return [lundi(p.du || base), decaler(lundi(p.du || base), 6)];
    if (p.type === "mois") return [premierDuMois(p.du || base), finDuMois(p.du || base)];
    return [p.du || base, p.au || p.du || base];
  }, [jour]);

  const chargerComplet = useCallback((p = periode) => {
    if (!jetonRef.current) return;
    const [du, au] = bornes(p);
    dernierPing.current = Date.now();
    axios
      .get(`${API}/mobile/caisse/complet`, { headers: h(), params: { du, au }, timeout: 25000 })
      .then((r) => {
        setVue(r.data);
        setErreur("");
      })
      .catch((err) => traiter(err, setErreur));
  }, [h, periode, bornes, traiter]);

  useEffect(() => {
    if (jeton) chargerJour();
  }, [jeton, chargerJour]);

  useEffect(() => {
    if (complet) chargerComplet(periode);
  }, [complet, periode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vue complète : 3 minutes sans toucher l'écran -> retour à la vue discrète
  useEffect(() => {
    if (!complet) return undefined;
    derniereAction.current = Date.now();
    const noter = () => {
      derniereAction.current = Date.now();
      if (Date.now() - dernierPing.current > 60000) chargerComplet();
    };
    const ev = ["pointerdown", "keydown", "scroll", "touchstart"];
    ev.forEach((n) => window.addEventListener(n, noter, { capture: true, passive: true }));
    const t = setInterval(() => {
      const r = COMPLET_MS - (Date.now() - derniereAction.current);
      setReste(Math.max(0, r));
      if (r <= 0 || document.visibilityState === "hidden") {
        setComplet(false);
        setVue(null);
        setMenu(false);
        setFeuille(null);
        axios.post(`${API}/mobile/caisse/discret`, {}, { headers: h(), timeout: 8000 }).catch(() => {});
        chargerJour();
      }
    }, 1000);
    return () => {
      clearInterval(t);
      ev.forEach((n) => window.removeEventListener(n, noter, { capture: true }));
    };
  }, [complet, h, chargerComplet, chargerJour]);

  const validerPin = (pin, secret) => {
    setOccupe(true);
    setErreurPin("");
    axios
      .post(`${API}/mobile/caisse/pin`, { pin, mot_secret: secret || "" }, { headers: entetes(code), timeout: 20000 })
      .then((r) => {
        jetonRef.current = r.data.jeton;
        setJeton(r.data.jeton);
        setEtat((e) => ({ ...e, pin_defini: true, secret_a_definir: false, bloque_min: 0 }));
      })
      .catch((err) => {
        const d = String(err?.response?.data?.detail || "");
        if (err?.response?.status === 401 && !d.startsWith("Code PIN")) {
          if (estNonAutorise(err)) onLogout();
          return;
        }
        setErreurPin(messageErreur(err));
      })
      .finally(() => setOccupe(false));
  };

  const rechercher = (e) => {
    e.preventDefault();
    const mot = recherche;
    if (!mot.trim() || complet) return;
    axios
      .post(`${API}/mobile/caisse/secret`, { mot }, { headers: h(), timeout: 15000 })
      .then((r) => {
        if (r.data && r.data.ok) {
          setRecherche("");
          setPeriode({ type: "jour", du: "", au: "" });
          setComplet(true);
        }
      })
      .catch((err) => traiter(err, setErreur));
  };

  const recharger = () => (complet ? chargerComplet() : chargerJour());

  const enregistrer = (donnees, ligne) => {
    setOccupe(true);
    setErreurFeuille("");
    const req = ligne
      ? axios.put(`${API}/mobile/caisse/mouvement/${ligne.id}`, donnees, { headers: h(), timeout: 20000 })
      : axios.post(`${API}/mobile/caisse/mouvement`, donnees, { headers: h(), timeout: 20000 });
    req
      .then(() => {
        setFeuille(null);
        recharger();
        if (complet) chargerJour();
      })
      .catch((err) => traiter(err, setErreurFeuille))
      .finally(() => setOccupe(false));
  };

  const supprimer = (ligne) => {
    setOccupe(true);
    axios
      .delete(`${API}/mobile/caisse/mouvement/${ligne.id}`, { headers: h(), timeout: 20000 })
      .then(() => {
        setFeuille(null);
        recharger();
      })
      .catch((err) => traiter(err, setErreurFeuille))
      .finally(() => setOccupe(false));
  };

  const action = (url, corps, apres) => {
    setOccupe(true);
    setErreurFeuille("");
    axios
      .post(`${API}${url}`, corps, { headers: h(), timeout: 20000 })
      .then((r) => {
        setFeuille(null);
        if (apres) apres(r.data);
        recharger();
      })
      .catch((err) => traiter(err, setErreurFeuille))
      .finally(() => setOccupe(false));
  };

  const exporterPdf = async () => {
    setMenu(false);
    const [du, au] = bornes(periode);
    try {
      const r = await axios.get(`${API}/mobile/caisse/pdf`, { headers: h(), params: { du, au }, responseType: "blob", timeout: 60000 });
      const nom = `Caisse-${du}${du === au ? "" : `-au-${au}`}.pdf`;
      const fichier = new File([r.data], nom, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [fichier] })) {
        await navigator.share({ files: [fichier], title: nom });
      } else {
        const url = URL.createObjectURL(fichier);
        const a = document.createElement("a");
        a.href = url;
        a.download = nom;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (err) {
      if (err?.name !== "AbortError") traiter(err, setErreur);
    }
  };

  const ouvrirHistorique = () => {
    setMenu(false);
    axios
      .get(`${API}/mobile/caisse/journal`, { headers: h(), timeout: 20000 })
      .then((r) => setFeuille({ type: "historique", journal: r.data.journal || [] }))
      .catch((err) => traiter(err, setErreur));
  };

  const filtre = (ls) => {
    const q = recherche.trim().toLowerCase();
    if (!q) return ls;
    return ls.filter((l) => `${l.client} ${l.categorie} ${l.detail} ${l.agent} ${l.mode}`.toLowerCase().includes(q));
  };

  const lignesJour = useMemo(() => filtre((jour && jour.lignes) || []), [jour, recherche]); // eslint-disable-line react-hooks/exhaustive-deps
  const lignesVue = useMemo(() => filtre((vue && vue.lignes) || []), [vue, recherche]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  if (!etat) {
    return (
      <div className="ecran flex items-center justify-center bg-[#0F172A] text-[#94A3B8]" data-testid="ecran-caisse">
        {erreur ? <p role="alert" className="text-sm px-6 text-center">{erreur}</p> : <Loader2 aria-hidden="true" className="w-6 h-6 animate-spin" />}
      </div>
    );
  }
  if (!jeton) {
    return <EcranPin key={etat.pin_defini ? "s" : "c"} etat={etat} onValider={validerPin} erreur={erreurPin} occupe={occupe} onRetour={onRetour} />;
  }

  const listes = complet && vue
    ? {
      entree: vue.listes.entree.filter((x) => x.actif).map((x) => x.libelle),
      sortie: vue.listes.sortie.filter((x) => x.actif).map((x) => x.libelle),
      modes: vue.listes.modes,
    }
    : jour && jour.listes;
  const cloture = jour && jour.cloture;
  const [du, au] = bornes(periode);
  const mm = Math.floor(reste / 60000);
  const ss = String(Math.floor((reste % 60000) / 1000)).padStart(2, "0");

  const barreRecherche = (
    <form onSubmit={rechercher} role="search" className="flex items-center gap-2 bg-[#1E293B] border border-[#334155] rounded-xl pl-3 pr-1 h-[42px] focus-within:border-[#2DD4BF]">
      <Search aria-hidden="true" className="w-[17px] h-[17px] text-[#94A3B8] flex-none" />
      <input
        type="search" enterKeyHint="search" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
        value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher dans la caisse…"
        aria-label="Rechercher dans la caisse" data-testid="caisse-recherche"
        className="flex-1 min-w-0 bg-transparent text-white text-[14px] placeholder:text-[#64748B] focus:outline-none"
      />
    </form>
  );

  // 2.6.6 : vue complète sur une journée passée -> saisies et clôture à la date affichée
  const jourCible = complet && periode.type === "jour" && jour && du && du !== jour.jour ? du : "";
  const boutonsSaisie = (
    <div className="flex gap-2">
      <button type="button" onClick={() => { setErreurFeuille(""); setFeuille({ type: "mvt", sens: "entree", jour: jourCible }); }} data-testid="caisse-plus"
        className={`flex-1 h-11 rounded-xl bg-[#0F766E] text-white font-bold text-[14px] flex items-center justify-center gap-1 ${FOCUS}`}>
        <Plus aria-hidden="true" className="w-4 h-4" /> Encaissement
      </button>
      <button type="button" onClick={() => { setErreurFeuille(""); setFeuille({ type: "mvt", sens: "sortie", jour: jourCible }); }} data-testid="caisse-moins"
        className={`flex-1 h-11 rounded-xl bg-[#B91C1C] text-white font-bold text-[14px] flex items-center justify-center gap-1 ${FOCUS}`}>
        <Minus aria-hidden="true" className="w-4 h-4" /> Sortie
      </button>
    </div>
  );

  const ouvrirLigne = (l) => {
    setErreurFeuille("");
    setFeuille({ type: "mvt", sens: l.sens, ligne: l });
  };

  const nbCredits = listeCredits ? listeCredits.length : (jour && jour.nb_credits) || 0;
  const barreOnglets = (
    <div className="flex-none flex gap-2" role="tablist">
      {[["caisse", complet ? "Caisse" : "Caisse du jour"], ["credits", `Crédits (${nbCredits})`]].map(([k, t]) => (
        <button key={k} type="button" role="tab" aria-selected={onglet === k} onClick={() => setOnglet(k)} data-testid={`onglet-${k}`}
          className={`flex-1 h-10 rounded-xl text-[13px] font-bold border ${onglet === k ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2E8F0]"} ${FOCUS}`}>
          {t}
        </button>
      ))}
    </div>
  );
  const creditsFiltres = (listeCredits || []).filter((c) => {
    const q = recherche.trim().toLowerCase();
    return !q || `${c.client} ${c.motif} ${c.remarque}`.toLowerCase().includes(q);
  });
  const totalCredits = (listeCredits || []).reduce((a, c) => a + c.montant, 0);
  const vueCredits = (
    <>
      <button type="button" onClick={() => { setErreurFeuille(""); setFeuille({ type: "credit" }); }} data-testid="credit-plus"
        className={`flex-none h-11 rounded-xl bg-[#6D28D9] text-white font-bold text-[14px] flex items-center justify-center gap-1 ${FOCUS}`}>
        <Plus aria-hidden="true" className="w-4 h-4" /> Crédit
      </button>
      {complet && listeCredits ? (
        <div className="flex-none bg-white border border-[#E2E8F0] rounded-xl px-3 py-2" data-testid="credits-total">
          <span className="text-[12px] text-[#475569]">Crédits en cours</span>
          <b className="block text-[18px] text-[#C2410C] tabular-nums">{da(totalCredits)}</b>
          <span className="text-[11.5px] text-[#475569]">
            {listeCredits.length} client{listeCredits.length > 1 ? "s" : ""} · dont fiches visa France{" "}
            {da(listeCredits.filter((c) => c.source === "fiche").reduce((a, c) => a + c.montant, 0))}
          </span>
        </div>
      ) : null}
      {!listeCredits ? (
        <p className="m-0 p-8 text-center text-sm text-[#475569] flex items-center justify-center gap-2">
          <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> Chargement…
        </p>
      ) : null}
      {listeCredits && !creditsFiltres.length ? <p className="m-0 p-6 text-center text-sm text-[#64748B]">Aucun crédit en cours.</p> : null}
      {creditsFiltres.map((c) => (
        <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-xl px-3 py-2.5" data-testid="credit">
          <div className="flex items-start gap-2">
            <span className="flex flex-col flex-1 min-w-0 gap-0.5">
              <span className="flex items-center gap-1.5 min-w-0">
                <span className={`text-[10.5px] font-bold px-1.5 rounded-md ${c.source === "fiche" ? "bg-[#CCFBF1] text-[#0F766E]" : "bg-[#EDE9FE] text-[#6D28D9]"}`}>
                  {c.source === "fiche" ? "Fiche" : c.motif}
                </span>
                <span className="text-[14px] font-semibold truncate">{c.client}</span>
              </span>
              <span className="text-[11.5px] text-[#64748B]">
                {c.source === "fiche" ? `${c.motif} · déposé le ${jourFr(c.date).slice(0, 5)}` : `ajouté par ${c.agent} le ${jourFr(c.date).slice(0, 5)}`}
                {c.remarque ? ` · ${c.remarque}` : ""}
              </span>
              {c.dernier_rappel ? (
                <span className={`text-[11px] ${c.dernier_rappel_ok === false ? "text-[#B91C1C]" : "text-[#0369A1]"}`} data-testid="credit-rappel">
                  {c.dernier_rappel_ok === false
                    ? `Rappel non envoyé le ${c.dernier_rappel} (${c.dernier_rappel_info || "erreur"})`
                    : `Rappel WhatsApp envoyé le ${c.dernier_rappel}${c.rappels > 1 ? ` · ${c.rappels} rappels` : ""}`}
                </span>
              ) : null}
            </span>
            <span className="flex-none text-[14px] font-extrabold text-[#C2410C] tabular-nums">{da(c.montant)}</span>
          </div>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => { setErreurFeuille(""); setFeuille({ type: "encaisser", credit: c }); }} data-testid="credit-encaisser"
              className={`flex-1 h-9 rounded-lg bg-[#0F766E] text-white text-[13px] font-bold ${FOCUS}`}>Encaisser</button>
            <button type="button" onClick={() => { setErreurFeuille(""); setFeuille({ type: "relancer", credit: c }); }} data-testid="credit-relancer"
              className={`flex-1 h-9 rounded-lg bg-[#E0F2FE] text-[#0369A1] text-[13px] font-bold ${FOCUS}`}>Relancer</button>
            {c.modifiable ? (
              <button type="button" onClick={() => { setErreurFeuille(""); setFeuille({ type: "credit", credit: c }); }}
                className={`flex-1 h-9 rounded-lg bg-[#E2E8F0] text-[13px] font-bold ${FOCUS}`}>Modifier</button>
            ) : null}
          </div>
        </div>
      ))}
    </>
  );
  const apresCredit = () => {
    chargerCredits();
    chargerJour();
    if (complet) chargerComplet();
  };

  return (
    <div className="ecran flex flex-col bg-[#F4F6F9] text-[#0F172A]" data-testid="ecran-caisse">
      {complet ? (
        <EnTete
          titre="Caisse · vue complète"
          sousTitre={<span className="text-[#5EEAD4]">retour vue discrète dans {mm}:{ss}</span>}
          onRetour={onRetour}
          droite={
            <button type="button" onClick={() => setMenu(true)} aria-label="Menu"
              className={`w-11 h-11 flex-none flex items-center justify-center text-[#E2E8F0] rounded-lg ${FOCUS}`}>
              <MoreHorizontal aria-hidden="true" className="w-5 h-5" />
            </button>
          }
          enfant={barreRecherche}
        />
      ) : (
        <EnTete titre="Caisse" sousTitre={jour ? `Aujourd'hui · ${jourFr(jour.jour)}` : ""} onRetour={onRetour} enfant={barreRecherche} />
      )}

      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 flex flex-col gap-2 pb-[max(16px,env(safe-area-inset-bottom))]">
        {erreur ? <p role="alert" className="m-0 p-3 text-center text-sm text-[#B91C1C]">{erreur}</p> : null}

        {barreOnglets}
        {onglet === "credits" ? vueCredits : !complet ? (
          <>
            {jour && cloture && cloture.faite ? (
              // 2.6.5 : après la clôture, le grand chiffre est l'argent remis (à garder / à verser)
              <div className="flex-none flex items-center justify-between bg-[#EDE9FE] border border-[#C4B5FD] rounded-xl px-3 py-2.5" data-testid="caisse-total-jour">
                <span className="flex flex-col">
                  <span className="text-[12.5px] text-[#5B21B6] font-semibold">Remis à la clôture</span>
                  <span className="text-[11.5px] text-[#6D28D9]">Journée clôturée à {cloture.quand}</span>
                </span>
                <b className="text-[19px] text-[#5B21B6] tabular-nums" data-testid="caisse-cloturee">{da(cloture.remis)}</b>
              </div>
            ) : null}
            {jour ? (
              <CasesSens lignes={jour.lignes} onChoisir={(k) => setFeuille({ type: "detail", sens: k })} />
            ) : null}
            {cloture && cloture.faite ? null : (
              boutonsSaisie
            )}
            {!jour ? (
              <p className="m-0 p-8 text-center text-sm text-[#475569] flex items-center justify-center gap-2">
                <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> Chargement…
              </p>
            ) : null}
            {jour && !lignesJour.length ? <p className="m-0 p-6 text-center text-sm text-[#64748B]">Aucun mouvement aujourd'hui.</p> : null}
            {lignesJour.map((l) => <Ligne key={l.id} l={l} onOuvrir={ouvrirLigne} />)}
            {jour && !(cloture && cloture.faite) ? (
              <button type="button" onClick={() => { setErreurFeuille(""); setFeuille({ type: "cloture" }); }} data-testid="caisse-cloturer"
                className={`mt-2 h-12 rounded-xl bg-[#6D28D9] text-white font-bold ${FOCUS}`}>
                Clôturer la journée
              </button>
            ) : null}
            <button type="button" onClick={() => { setErreurFeuille(""); setFeuille({ type: "pin" }); }}
              className={`self-center mt-2 text-xs text-[#64748B] underline ${FOCUS}`}>
              Changer mon code PIN
            </button>
          </>
        ) : (
          <>
            <div className="flex-none flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
              {[["jour", "Jour"], ["semaine", "Semaine"], ["mois", "Mois"], ["periode", "Période"]].map(([k, t]) => (
                <button key={k} type="button" aria-pressed={periode.type === k}
                  onClick={() => setPeriode({ type: k, du: k === "periode" ? du : "", au: k === "periode" ? au : "" })}
                  className={`flex-none px-3.5 h-9 rounded-full text-[13px] font-semibold border ${periode.type === k ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2E8F0]"} ${FOCUS}`}>
                  {t}
                </button>
              ))}
            </div>
            {periode.type === "periode" ? (
              <div className="flex gap-2 items-center text-[13px]">
                <input type="date" className={`${CLS_INPUT} h-10 flex-1 min-w-0`} value={du} onChange={(e) => setPeriode({ ...periode, du: e.target.value })} aria-label="Du" />
                <span>au</span>
                <input type="date" className={`${CLS_INPUT} h-10 flex-1 min-w-0`} value={au} onChange={(e) => setPeriode({ ...periode, au: e.target.value })} aria-label="Au" />
              </div>
            ) : (
              <div className="flex items-center justify-between bg-white border border-[#E2E8F0] rounded-xl h-10 px-1">
                <button type="button" aria-label="Précédent" className={`w-10 h-10 flex items-center justify-center ${FOCUS}`}
                  onClick={() => setPeriode({ ...periode, du: periode.type === "jour" ? decaler(du, -1) : periode.type === "semaine" ? decaler(du, -7) : decaler(premierDuMois(du), -1) })}>
                  <ChevronLeft aria-hidden="true" className="w-5 h-5" />
                </button>
                <span className="text-[13.5px] font-semibold">{du === au ? jourFr(du) : `${jourFr(du)} → ${jourFr(au)}`}</span>
                <button type="button" aria-label="Suivant" className={`w-10 h-10 flex items-center justify-center ${FOCUS}`}
                  onClick={() => setPeriode({ ...periode, du: periode.type === "jour" ? decaler(du, 1) : periode.type === "semaine" ? decaler(du, 7) : decaler(au, 1) })}>
                  <ChevronRight aria-hidden="true" className="w-5 h-5" />
                </button>
              </div>
            )}
            {!vue ? (
              <p className="m-0 p-8 text-center text-sm text-[#475569] flex items-center justify-center gap-2">
                <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> Chargement…
              </p>
            ) : (
              <>
                {(() => {
                  // 2.6.5 : argent réellement remis par Moha (à garder ou à verser à la banque)
                  const remis = vue.clotures.reduce((a, c) => a + (Number(c.remis) || 0), 0);
                  const calcule = vue.clotures.reduce((a, c) => a + (Number(c.calcule) || 0), 0);
                  const ecart = remis - calcule;
                  const n = vue.clotures.length;
                  return (
                    <div className="flex-none bg-[#0F172A] text-white rounded-xl px-3.5 py-3" data-testid="caisse-a-verser">
                      <span className="text-[12px] text-[#94A3B8] font-semibold">À garder / à verser</span>
                      <b className="block text-[24px] tabular-nums leading-tight">{da(remis)}</b>
                      <span className="text-[11.5px] text-[#CBD5E1]">
                        {n
                          ? `Remis par Moha · ${n} journée${n > 1 ? "s" : ""} clôturée${n > 1 ? "s" : ""} · calculé ${da(calcule)} · `
                          : "Aucune journée clôturée sur la période"}
                        {n ? <span className={ecart === 0 ? "text-[#86EFAC]" : "text-[#FCA5A5]"}>écart {ecart ? da(ecart) : "0 DA"}</span> : null}
                      </span>
                    </div>
                  );
                })()}
                <div className="grid grid-cols-2 gap-2" data-testid="caisse-totaux">
                  <div className="bg-white border border-[#E2E8F0] rounded-xl px-3 py-2"><span className="text-[11.5px] text-[#475569]">Entrées</span><b className="block text-[16px] text-[#15803D] tabular-nums">{da(vue.totaux.entrees)}</b></div>
                  <div className="bg-white border border-[#E2E8F0] rounded-xl px-3 py-2"><span className="text-[11.5px] text-[#475569]">Sorties</span><b className="block text-[16px] text-[#B91C1C] tabular-nums">{da(vue.totaux.sorties)}</b></div>
                  <div className="bg-white border border-[#E2E8F0] rounded-xl px-3 py-2"><span className="text-[11.5px] text-[#475569]">Solde espèces</span><b className="block text-[16px] text-[#0F766E] tabular-nums" data-testid="caisse-solde">{da(vue.totaux.solde_especes)}</b></div>
                  <div className="bg-white border border-[#E2E8F0] rounded-xl px-3 py-2"><span className="text-[11.5px] text-[#475569]">Hors espèces</span><b className="block text-[16px] text-[#0369A1] tabular-nums">{da(vue.totaux.total_hors_especes)}</b>
                    {vue.totaux.hors_especes.map(([m, v]) => <span key={m} className="block text-[10.5px] text-[#475569]">{m} {da(v)}</span>)}</div>
                </div>
                {vue.clotures.map((c) => (
                  <div key={c.jour} className={`rounded-xl px-3 py-2 text-[13px] border ${c.ecart === 0 ? "bg-[#DCFCE7] border-[#86EFAC]" : "bg-[#FFEDD5] border-[#FDBA74]"}`} data-testid="caisse-ecart">
                    <div className="flex justify-between"><span>Clôture {jourFr(c.jour).slice(0, 5)} · {c.par} {c.quand.slice(-5)}</span>
                      <button type="button" onClick={() => setFeuille({ type: "rouvrir", jour: c.jour })} className={`text-[12px] font-semibold text-[#0F766E] flex items-center gap-1 ${FOCUS}`}>
                        <RotateCcw aria-hidden="true" className="w-3.5 h-3.5" /> Rouvrir</button></div>
                    <div className="flex justify-between"><span>Calculé</span><b className="tabular-nums">{da(c.calcule)}</b></div>
                    <div className="flex justify-between"><span>Remis</span><b className="tabular-nums">{da(c.remis)}</b></div>
                    <div className={`flex justify-between font-bold ${c.ecart === 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}><span>{c.ecart === 0 ? "✅ Écart" : "⚠️ Écart"}</span><span className="tabular-nums">{c.ecart ? da(c.ecart) : "0 DA"}</span></div>
                  </div>
                ))}
                {periode.type === "jour" && !vue.clotures.length ? (
                  jourCible ? (
                    <button type="button" onClick={() => { setErreurFeuille(""); setFeuille({ type: "cloture", jour: jourCible }); }}
                      data-testid="caisse-cloturer-passe" className={`h-11 rounded-xl bg-[#6D28D9] text-white font-bold text-[14px] ${FOCUS}`}>
                      Clôturer la journée du {jourFr(jourCible)}
                    </button>
                  ) : (
                    <p className="m-0 text-[12.5px] text-[#64748B] text-center">Journée pas encore clôturée.</p>
                  )
                ) : null}
                {boutonsSaisie}
                <Bloc titre="Entrées par service" lignes={vue.totaux.par_service} />
                <Bloc titre="Sorties par motif" lignes={vue.totaux.par_motif} />
                <Bloc titre="Entrées par mode" lignes={vue.totaux.par_mode} />
                <Bloc titre="Entrées par agent" lignes={vue.totaux.par_agent} />
                <h3 className="m-0 mt-1 text-[13px] font-bold">Mouvements ({lignesVue.length})</h3>
                {lignesVue.map((l) => <Ligne key={l.id} l={l} complet avecJour={du !== au} onOuvrir={ouvrirLigne} />)}
              </>
            )}
          </>
        )}
      </main>

      {menu ? (
        <Feuille titre="Caisse" onFermer={() => setMenu(false)}>
          <div className="flex flex-col gap-1">
            {[
              [FileDown, "Exporter en PDF", exporterPdf],
              [ListChecks, "Services et motifs", () => { setMenu(false); setFeuille({ type: "listes" }); }],
              [History, "Historique des suppressions", ouvrirHistorique],
              [KeyRound, "Changer le mot secret", () => { setMenu(false); setErreurFeuille(""); setFeuille({ type: "secret" }); }],
              [Lock, "Réinitialiser le PIN de Moha", () => { setMenu(false); setErreurFeuille(""); setFeuille({ type: "reinit" }); }],
              [EyeOff, "Revenir à la vue discrète", () => {
                setMenu(false);
                setComplet(false);
                setVue(null);
                axios.post(`${API}/mobile/caisse/discret`, {}, { headers: h(), timeout: 8000 }).catch(() => {});
              }],
            ].map(([Icone, t, f]) => (
              <button key={t} type="button" onClick={f} className={`h-12 flex items-center gap-3 px-2 rounded-lg text-left text-[15px] active:bg-[#F1F5F9] ${FOCUS}`}>
                <Icone aria-hidden="true" className="w-5 h-5 text-[#475569]" /> {t}
              </button>
            ))}
          </div>
        </Feuille>
      ) : null}

      {feuille && feuille.type === "mvt" ? (
        <FormMouvement
          sens={feuille.sens} ligne={feuille.ligne} listes={listes} occupe={occupe} erreur={erreurFeuille}
          jourFixe={feuille.ligne ? "" : feuille.jour} dateModifiable={complet && feuille.ligne && feuille.ligne.source === "caisse"}
          onFermer={() => setFeuille(null)}
          onEnregistrer={(d) => enregistrer(d, feuille.ligne)}
          onSupprimer={feuille.ligne ? () => supprimer(feuille.ligne) : null}
        />
      ) : null}
      {feuille && feuille.type === "credit" ? (
        <FormCredit credit={feuille.credit} motifs={(listes && listes.entree) || []} occupe={occupe} erreur={erreurFeuille}
          onFermer={() => setFeuille(null)}
          onEnregistrer={(d) => {
            setOccupe(true);
            setErreurFeuille("");
            const req = feuille.credit
              ? axios.put(`${API}/mobile/caisse/credit/${feuille.credit.id}`, d, { headers: h(), timeout: 20000 })
              : axios.post(`${API}/mobile/caisse/credit`, d, { headers: h(), timeout: 20000 });
            req.then(() => { setFeuille(null); apresCredit(); })
              .catch((err) => traiter(err, setErreurFeuille))
              .finally(() => setOccupe(false));
          }}
          onSupprimer={feuille.credit ? () => {
            setOccupe(true);
            axios.delete(`${API}/mobile/caisse/credit/${feuille.credit.id}`, { headers: h(), timeout: 20000 })
              .then(() => { setFeuille(null); apresCredit(); })
              .catch((err) => traiter(err, setErreurFeuille))
              .finally(() => setOccupe(false));
          } : null} />
      ) : null}
      {feuille && feuille.type === "relancer" ? (
        <Feuille titre={`Relancer · ${feuille.credit.client}`} onFermer={() => setFeuille(null)}>
          <p className="m-0 text-[13px] text-[#475569]">
            Un rappel WhatsApp part tout de suite vers le client : il lui reste <b>{da(feuille.credit.montant)}</b> à régler
            ({feuille.credit.motif}).
          </p>
          {feuille.credit.dernier_rappel ? (
            <p className="mt-2 mb-0 text-[12.5px] text-[#475569]">Dernier rappel : {feuille.credit.dernier_rappel}</p>
          ) : null}
          {erreurFeuille ? <p role="alert" className="text-[#B91C1C] text-sm mt-3 mb-0">{erreurFeuille}</p> : null}
          <button type="button" disabled={occupe} data-testid="relancer-valider"
            onClick={() => {
              setOccupe(true);
              setErreurFeuille("");
              axios.post(`${API}/mobile/caisse/credit/relancer`, { id: feuille.credit.id }, { headers: h(), timeout: 30000 })
                .then(() => { setFeuille(null); chargerCredits(); })
                .catch((err) => traiter(err, setErreurFeuille))
                .finally(() => setOccupe(false));
            }}
            className={`w-full mt-4 h-12 rounded-xl bg-[#0369A1] text-white font-bold disabled:opacity-50 ${FOCUS}`}>
            {occupe ? "…" : "Envoyer le rappel"}
          </button>
        </Feuille>
      ) : null}
      {feuille && feuille.type === "encaisser" ? (
        <FormEncaisser credit={feuille.credit} modes={(listes && listes.modes) || [ESPECES]} occupe={occupe} erreur={erreurFeuille}
          onFermer={() => setFeuille(null)}
          onValider={(montant, mode) => {
            setOccupe(true);
            setErreurFeuille("");
            axios.post(`${API}/mobile/caisse/credit/encaisser`, { id: feuille.credit.id, montant, mode }, { headers: h(), timeout: 20000 })
              .then(() => { setFeuille(null); apresCredit(); })
              .catch((err) => traiter(err, setErreurFeuille))
              .finally(() => setOccupe(false));
          }} />
      ) : null}
      {feuille && feuille.type === "cloture" ? (
        <FormCloture occupe={occupe} erreur={erreurFeuille} onFermer={() => setFeuille(null)} jourFixe={feuille.jour}
          lignes={feuille.jour ? (vue && vue.lignes ? vue.lignes.filter((l) => l.jour === feuille.jour) : null) : (jour ? jour.lignes : null)}
          onValider={(remis) => action("/mobile/caisse/cloture", feuille.jour ? { remis, jour: feuille.jour } : { remis }, () => chargerJour())} />
      ) : null}
      {feuille && feuille.type === "detail" && jour ? (
        <Feuille titre={feuille.sens === "sortie" ? "Sorties du jour" : "Entrées du jour"} onFermer={() => setFeuille(null)}>
          <DetailSens lignes={jour.lignes} sens={feuille.sens} />
        </Feuille>
      ) : null}
      {feuille && feuille.type === "pin" ? (
        <FormChangerPin occupe={occupe} erreur={erreurFeuille} onFermer={() => setFeuille(null)}
          onValider={(ancien, nouveau) => action("/mobile/caisse/pin/changer", { ancien, nouveau })} />
      ) : null}
      {feuille && feuille.type === "rouvrir" ? (
        <Feuille titre={`Rouvrir la journée du ${jourFr(feuille.jour)}`} onFermer={() => setFeuille(null)}>
          <p className="m-0 text-[13px] text-[#475569]">La clôture sera annulée (gardée dans l'historique). Moha pourra de nouveau saisir et clôturer ce jour-là s'il s'agit d'aujourd'hui.</p>
          {erreurFeuille ? <p role="alert" className="text-[#B91C1C] text-sm mt-3 mb-0">{erreurFeuille}</p> : null}
          <button type="button" disabled={occupe} onClick={() => action("/mobile/caisse/rouvrir", { jour: feuille.jour }, () => chargerJour())}
            className={`w-full mt-4 h-12 rounded-xl bg-[#0F766E] text-white font-bold ${FOCUS}`}>Rouvrir</button>
        </Feuille>
      ) : null}
      {feuille && feuille.type === "secret" ? (
        <FeuilleTexte titre="Changer le mot secret" label="Nouveau mot secret (au moins 4 caractères)" motDePasse
          occupe={occupe} erreur={erreurFeuille} onFermer={() => setFeuille(null)}
          onValider={(mot) => action("/mobile/caisse/secret/changer", { mot })} />
      ) : null}
      {feuille && feuille.type === "reinit" ? (
        <Feuille titre="Réinitialiser le PIN de Moha" onFermer={() => setFeuille(null)}>
          <p className="m-0 text-[13px] text-[#475569]">Moha choisira un nouveau code PIN à sa prochaine ouverture de la caisse.</p>
          {erreurFeuille ? <p role="alert" className="text-[#B91C1C] text-sm mt-3 mb-0">{erreurFeuille}</p> : null}
          <button type="button" disabled={occupe} onClick={() => action("/mobile/caisse/pin/reinitialiser", { identifiant: "moha" })}
            className={`w-full mt-4 h-12 rounded-xl bg-[#B91C1C] text-white font-bold ${FOCUS}`}>Réinitialiser</button>
        </Feuille>
      ) : null}
      {feuille && feuille.type === "listes" && vue ? (
        <FeuilleListes listes={vue.listes} occupe={occupe} erreur={erreurFeuille} onFermer={() => setFeuille(null)}
          onChanger={(sens, libelle, actif) => {
            setOccupe(true);
            setErreurFeuille("");
            axios.post(`${API}/mobile/caisse/liste`, { sens, libelle, actif }, { headers: h(), timeout: 20000 })
              .then((r) => setVue((v) => ({ ...v, listes: r.data })))
              .catch((err) => traiter(err, setErreurFeuille))
              .finally(() => setOccupe(false));
          }} />
      ) : null}
      {feuille && feuille.type === "historique" ? (
        <Feuille titre="Historique" onFermer={() => setFeuille(null)}>
          {!feuille.journal.length ? <p className="text-sm text-[#64748B]">Aucune suppression ni modification.</p> : null}
          <div className="flex flex-col gap-2">
            {feuille.journal.map((j, i) => <LigneJournal key={i} j={j} />)}
          </div>
        </Feuille>
      ) : null}
    </div>
  );
}

function FeuilleTexte({ titre, label, motDePasse, occupe, erreur, onFermer, onValider }) {
  const [v, setV] = useState("");
  return (
    <Feuille titre={titre} onFermer={onFermer}>
      <Champ label={label}>
        <input className={CLS_INPUT} type={motDePasse ? "password" : "text"} autoComplete="off" value={v} onChange={(e) => setV(e.target.value)} />
      </Champ>
      {erreur ? <p role="alert" className="text-[#B91C1C] text-sm mt-3 mb-0">{erreur}</p> : null}
      <button type="button" disabled={v.trim().length < 4 || occupe} onClick={() => onValider(v)}
        className={`w-full mt-4 h-12 rounded-xl bg-[#0F766E] text-white font-bold disabled:opacity-50 ${FOCUS}`}>Enregistrer</button>
    </Feuille>
  );
}

function FeuilleListes({ listes, occupe, erreur, onFermer, onChanger }) {
  const [nouveau, setNouveau] = useState({ entree: "", sortie: "" });
  return (
    <Feuille titre="Services et motifs" onFermer={onFermer}>
      {[["entree", "Services (encaissements)"], ["sortie", "Motifs de sortie"]].map(([sens, titre]) => (
        <section key={sens} className="mb-4">
          <h3 className="m-0 mb-1.5 text-[13px] font-bold">{titre}</h3>
          {listes[sens].map((x) => (
            <label key={x.libelle} className="flex items-center justify-between h-10 border-b border-[#F1F5F9] text-[14px]">
              <span className={x.actif ? "" : "text-[#94A3B8] line-through"}>{x.libelle}</span>
              <input type="checkbox" checked={x.actif} disabled={occupe} onChange={(e) => onChanger(sens, x.libelle, e.target.checked)} className="w-5 h-5 accent-[#0F766E]" />
            </label>
          ))}
          <div className="flex gap-2 mt-2">
            <input className={`${CLS_INPUT} flex-1 min-w-0 h-10`} placeholder="Ajouter…" value={nouveau[sens]}
              onChange={(e) => setNouveau({ ...nouveau, [sens]: e.target.value })} />
            <button type="button" disabled={!nouveau[sens].trim() || occupe}
              onClick={() => { onChanger(sens, nouveau[sens], true); setNouveau({ ...nouveau, [sens]: "" }); }}
              className={`h-10 px-4 rounded-[10px] bg-[#0F766E] text-white font-bold disabled:opacity-50 ${FOCUS}`}>Ajouter</button>
          </div>
        </section>
      ))}
      {erreur ? <p role="alert" className="text-[#B91C1C] text-sm m-0">{erreur}</p> : null}
    </Feuille>
  );
}

const ACTIONS = {
  suppression: "Suppression", modification: "Modification", reouverture: "Réouverture de journée",
  pin_bloque: "PIN bloqué (5 erreurs)", pin_reinitialise: "PIN réinitialisé",
  credit_modification: "Crédit modifié", credit_suppression: "Crédit supprimé",
};

function LigneJournal({ j }) {
  const d = j.detail || {};
  let texte = "";
  if (j.action === "suppression") texte = `${d.categorie || ""} · ${d.client || d.beneficiaire || ""} · ${da(d.montant)} (saisi par ${d.agent || "?"}, ${jourFr(String(d.jour || "")).slice(0, 5)})`;
  else if (j.action === "modification") texte = `${(d.avant || {}).categorie || ""} · ${da((d.avant || {}).montant)} → ${da((d.apres || {}).montant)}`;
  else if (j.action === "credit_suppression") texte = `${d.motif || ""} · ${d.client || ""} · reste ${da(d.reste)} (ajouté par ${d.agent || "?"})`;
  else if (j.action === "credit_modification") texte = `${(d.avant || {}).client || ""} · ${da((d.avant || {}).reste)} → ${da((d.apres || {}).montant)}`;
  else if (j.action === "reouverture") texte = `Journée du ${jourFr(d.jour)} · remis ${da(d.remis)} par ${d.par || "?"}`;
  else texte = typeof d === "string" ? d : "";
  return (
    <div className="border border-[#E2E8F0] rounded-xl px-3 py-2 text-[13px]">
      <div className="flex justify-between gap-2"><b>{ACTIONS[j.action] || j.action}</b><span className="text-[#64748B] text-xs">{j.quand}</span></div>
      <div className="text-[#475569]">{texte}</div>
      <div className="text-[11.5px] text-[#64748B]">par {j.par}</div>
    </div>
  );
}
